import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Card } from 'grommet';
import { Deliver, Organization, Tasks, Trigger } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { EquipmentImporterService } from '/src/api/services/EquipmentImporterService';
import { FacilityImporterService } from '/src/api/services/FacilitiesImporterService';
import { KwhImporterService } from '/src/api/services/KwhImporterService';
import { StatusImporterService } from '/src/api/services/StatusImporterService';
import { FooterNav, HeaderNav, Text } from '/src/components';
import AdminSideNav from '/src/components/admin/AdminSideNav';
import { FileImporterCard } from '/src/components/admin/FileImporterCard';
import { useDashboardStore } from '/src/context';
import { useImporter } from '/src/hooks/useImporter';

const FuseAdminImporter = observer(() => {
  const dashboardStore = useDashboardStore();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = searchParams.get('tab') || 'kwh';

  const kwhImporter = useImporter({
    importerService: KwhImporterService,
  });

  const statusImporter = useImporter({
    importerService: StatusImporterService,
  });

  const equipmentImporter = useImporter({
    importerService: EquipmentImporterService,
  });

  const facilityImporter = useImporter({
    importerService: FacilityImporterService,
  });

  useEffect(() => {
    dashboardStore.getAdminView();
    return () => dashboardStore.clearDashboardData();
  }, []);

  useEffect(() => {
    if (!searchParams.get('tab')) {
      setActiveTab('kwh');
    }
  }, [location.search]);

  interface TabButtonProps {
    label: string;
    isActive: boolean;
    onClick: () => void;
    icon: React.ReactNode;
  }

  const setActiveTab = (tab: string) => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('tab', tab);
    navigate(`?${newSearchParams.toString()}`, { replace: true });
  };

  const TabButton = ({ label, isActive, onClick, icon }: TabButtonProps) => (
    <Box
      pad={{ vertical: 'medium', horizontal: 'large' }}
      onClick={onClick}
      focusIndicator={false}
      hoverIndicator={{ color: 'light-2', opacity: 'weak' }}
      style={{
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        borderBottom: isActive ? '2px solid #00739D' : '2px solid transparent',
        background: isActive ? 'white' : 'transparent',
        borderRadius: '8px 8px 0 0',
        minWidth: '140px',
        textAlign: 'center',
      }}
    >
      <Box align="center" margin={{ bottom: 'xsmall' }}>
        {icon}
      </Box>
      <Text
        color={isActive ? 'brand' : 'dark-3'}
        size="medium"
        weight={isActive ? 'bold' : 'normal'}
        style={{
          letterSpacing: '0.5px',
          textAlign: 'center',
        }}
      >
        {label}
      </Text>
    </Box>
  );

  const importers = [
    {
      key: 'kwh',
      label: 'kWh Importer',
      description: 'Upload an Excel file containing kWh data for equipment',
      Icon: Trigger,
      importer: kwhImporter,
    },
    {
      key: 'status',
      label: 'Status Importer',
      description: 'Upload an Excel file containing fse registration status data for equipment',
      Icon: Tasks,
      importer: statusImporter,
    },
    {
      key: 'equipment',
      label: 'Equipment Importer',
      description: 'Upload an Excel file containing new equipment data',
      Icon: Deliver,
      importer: equipmentImporter,
    },
    {
      key: 'facility',
      label: 'Facility Importer',
      description: 'Upload an Excel file containing new facility data',
      Icon: Organization,
      importer: facilityImporter,
    },
  ];

  const activeImporter = importers.find(({ key }) => key === activeTab);

  return (
    <Box height={{ min: '100vh' }} background="light" style={{ display: 'flex', flexDirection: 'column' }}>
      <HeaderNav />
      <Box flex="grow" style={{ display: 'flex', flexDirection: 'row', minHeight: 0 }}>
        <AdminSideNav />
        <Box flex="grow" pad={{ horizontal: 'large', vertical: 'medium' }} overflow="auto">
          <Box flex={false} gap="large">
            <Box
              direction="row"
              align="center"
              justify="between"
              margin={{ vertical: 'medium' }}
              pad={{ horizontal: 'small' }}
            >
              <Text size="xlarge" weight="normal">
                File Importer
              </Text>
            </Box>

            <Card background="white" elevation="none" round="xsmall" style={{ marginBottom: -20 }}>
              <Box direction="row">
                {importers.map(({ key, label, Icon }) => (
                  <TabButton
                    key={key}
                    label={label}
                    isActive={activeTab === key}
                    onClick={() => setActiveTab(key)}
                    icon={<Icon size="24px" color={activeTab === key ? '#00739D' : '#CCCCCC'} />}
                  />
                ))}
              </Box>
            </Card>

            {activeImporter && (
              <FileImporterCard
                title={activeImporter.label}
                description={activeImporter.description}
                onFileSelect={activeImporter.importer.handleFileSelect}
                onUpload={activeImporter.importer.handleUpload}
                isUploading={activeImporter.importer.isUploading}
                selectedFile={activeImporter.importer.selectedFile}
                validationErrors={activeImporter.importer.validationErrors}
                successMessage={activeImporter.importer.successMessage}
              />
            )}
          </Box>
        </Box>
      </Box>
      <Box flex={false}>
        <FooterNav />
      </Box>
    </Box>
  );
});

export default FuseAdminImporter;
